import { EXIT_DATE_ALIAS, FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { GRID_DATE, NUMBER } from 'common/constants/gridType';
import { getEnterpriseValueFormula, getEquityValueFormula } from 'common/formulas/math.js/ENTERPRISE_AND_EQUITY_VALUE';
import { GridType } from 'common/types/scalarSpreadsheet';
import { ICell, ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import {
  contentClass,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
  LESS_DEBT_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  FundExchangeRateRowLabel
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateRowLabel/FundExchangeRateRowLabel';
import FundExchangeRateValueViewer
  from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateValueViewer/FundExchangeRateValueViewer';

// Special alias for the top-row that shows Fund Currency selector in Column C
const FUND_CURRENCY_SELECTOR_ALIAS = 'fund_currency_selector';

const InputSheetParser = ({ rowConfig }: ParserParams<object, object, object, object>) => {
  const cells: Record<string, ICell> = {};
  let enterpriseValueCell: ICell | undefined;
  let equityValueCell: ICell | undefined;
  let plusCashCell: ICell | undefined;
  let lessDebtCell: ICell | undefined;

  // We now create three columns per row: A (label), B (company currency), C (fund currency)
  rowConfig.forEach((row, index) => {
    // calculate alphanumeric value for the index
    const columnLegend = 'A';
    const valueColumnLegend = 'B';
    const fundValueColumnLegend = 'C';
    const key = `${columnLegend}${index + 1}`;
    const valueKey = `${valueColumnLegend}${index + 1}`;
    const fundValueKey = `${fundValueColumnLegend}${index + 1}`;
    let gridType;
    let fundValueViewer = row.valueViewer
    let fundValueExpr = `=${valueKey}`
    if ([PLUS_CASH_ALIAS, LESS_DEBT_ALIAS, EXIT_ENTERPRISE_VALUE_ALIAS, EXIT_EQUITY_ALIAS, FIRM_TOTAL_EXIT_PROCEEDS_ALIAS].includes(row.alias)) {
      fundValueViewer = FundExchangeRateValueViewer;
      fundValueExpr = `=${valueKey} * ExchangeRateSheet.fundExchangeRate`
      gridType = NUMBER;
    } else if (row.alias === EXIT_DATE_ALIAS) {
      gridType = GRID_DATE;
    }
    cells[key] = {
      key,
      columnLegend,
      tooltipMessages: row.tooltipMessages,
      alias: row.headerAlias || '',
      value: row.headerValue,
      valueViewer: undefined,
      dataEditor: undefined,
      readOnly: true,
      className: 'row-label',
      expr: undefined,
    };
    const valueCell = {
      value: row.value,
      tooltipMessages: [],
      key: valueKey,
      columnLegend: valueColumnLegend,
      className: contentClass,
      alias: row.alias,
      readOnly: [FIRM_TOTAL_EXIT_PROCEEDS_ALIAS].includes(row.alias),
      valueViewer: row.valueViewer,
      dataEditor: row.dataEditor,
      gridDateComponent: row.gridDateComponent,
      format: row.format,
      gridType: row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS ? 'number' : (gridType as GridType),
      expr: row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS ? row.expr : undefined,
      customKey: row.customKey,
    };
    cells[valueKey] = valueCell;

    // Build Column C (fund currency value/controls)
    const fundValueCell: ICell = {
      value: fundValueExpr,
      tooltipMessages: [],
      key: fundValueKey,
      columnLegend: fundValueColumnLegend,
      className: contentClass,
      alias: `${row.alias}_fund`,
      readOnly: true,
      dataEditor: undefined,
      format: row.format,
      gridType: (gridType as GridType),
      expr: undefined,
      style: {
        display: row.hidden ? 'none' : 'table-cell',
      },
      valueViewer: fundValueViewer,
    };

    switch (row.alias) {
      case EXIT_ENTERPRISE_VALUE_ALIAS:
        enterpriseValueCell = valueCell;
        // Show fund value viewer in Column C for EV
        fundValueCell.alias = FUND_EXIT_ENTERPRISE_ALIAS;
        fundValueCell.readOnly = false;
        fundValueCell.value = `=${valueCell.key} * ExchangeRateSheet.fundExchangeRate`;
        break;
      case EXIT_EQUITY_ALIAS:
        equityValueCell = valueCell;
        // Show fund value viewer in Column C for Equity

        fundValueCell.alias = FUND_EXIT_EQUITY_ALIAS;
        fundValueCell.readOnly = false;
        fundValueCell.value = `=${valueCell.key} * ExchangeRateSheet.fundExchangeRate`;
        break;
      case PLUS_CASH_ALIAS:
        plusCashCell = valueCell;
        valueCell.style = row.style;
        cells[key].className = row.className
        break;
      case LESS_DEBT_ALIAS:
        lessDebtCell = valueCell;
        valueCell.style = row.style;
        cells[key].className = row.className
        break;
      case FUND_CURRENCY_SELECTOR_ALIAS:
        // For the special top row, render the selector/label in Column C
        fundValueCell.valueViewer = FundExchangeRateRowLabel;

        break;

      default:
        break;
    }

    // Always assign the fund column cell (it may be empty for most rows)
    cells[fundValueKey] = fundValueCell;
  });
  if (enterpriseValueCell?.key && equityValueCell?.key && plusCashCell?.key && lessDebtCell?.key) {
    if (enterpriseValueCell.value && !equityValueCell.value) {
      equityValueCell.expr = `=${getEquityValueFormula(enterpriseValueCell.key, plusCashCell.key, lessDebtCell.key)}`;
    }
    if (equityValueCell.value && !enterpriseValueCell.value) {
      enterpriseValueCell.expr =  `=${getEnterpriseValueFormula(equityValueCell.key, plusCashCell.key, lessDebtCell.key)}`;
    }
  }
  return cells;
};

export default InputSheetParser;
